/**
 * Config Column Factory
 * Creates dynamic columns based on config options
 */
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import { formatNumberUsage } from '@/utils/function'
import type { ServiceDetailRow } from './types'
import { sumConfigValue, hasConfigInRows } from './utils'

// ============================================================================
// CONFIG COLUMN OPTIONS
// ============================================================================

export interface ConfigColumnOptions {
    key: string
    title: string
    configName: string | string[]
    width?: number
    useCapacityCommitment?: boolean
    formatter?: (value: number) => string
}

// ============================================================================
// CONFIG COLUMN FACTORY
// ============================================================================

/**
 * Create a single config column
 */
export const createConfigColumn = (
    options: ConfigColumnOptions,
    rows: ServiceDetailRow[]
): DataTableColumn<ServiceDetailRow> | null => {
    const {
        key,
        title,
        configName,
        width = 100,
        useCapacityCommitment = false,
        formatter = formatNumberUsage,
    } = options
    const names = Array.isArray(configName) ? configName : [configName]

    // Check if any row has this config
    const hasConfig = names.some((name) => hasConfigInRows(rows, name))
    if (!hasConfig) return null

    return {
        key,
        title,
        width,
        cellClassName: 'text-right',
        render: (_, row) => {
            // Sum values from all matching config names
            let value = 0
            for (const name of names) {
                const v = sumConfigValue(
                    row.configoptions,
                    name,
                    useCapacityCommitment,
                    row.service_template
                )
                if (v) value += v
            }

            if (row._rowType === 'total') {
                return value ? (
                    <span className="font-medium text-primary">
                        {formatter(value)}
                    </span>
                ) : (
                    '-'
                )
            }
            return value ? formatter(value) : '-'
        },
    }
}

/**
 * Create multiple config columns, filtering out null ones
 */
export const createConfigColumns = (
    configs: ConfigColumnOptions[],
    rows: ServiceDetailRow[]
): DataTableColumn<ServiceDetailRow>[] => {
    return configs
        .map((config) => createConfigColumn(config, rows))
        .filter((col): col is DataTableColumn<ServiceDetailRow> => col !== null)
}
